import styled from '@emotion/styled'
import { Container } from '@mui/material'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useMutation, useReactiveVar } from '@apollo/client'

import { Meta } from 'src/system/Meta'
import { setMatchesViewModeMutation } from 'src/graphql'
import type {
  SetMatchesViewModeMutation,
  SetMatchesViewModeMutationVariables,
  UserInterfaceViewMode as ViewMode
} from 'src/graphql/types'
import { UserAppBar } from 'src/system/app-bar/user/UserAppBar'
import { Background } from 'src/system/ui/layout/Background'
import { PageTitle } from 'src/system/ui/typography/PageTitle'
import { PageSubhead } from 'src/system/ui/typography/PageSubhead'
import { SetViewModeButtons } from 'src/screens/matches/SetViewModeButtons'
import { SourceEmptyState } from 'src/screens/matches/SourceEmptyState'
import { SourceTabs } from 'src/screens/matches/SourceTabs'
import { activeMatchSource, useMatches } from 'src/hooks/use-matches'
import { useMaybeUser } from 'src/hooks/use-user'
import { useProfile } from 'src/hooks/use-profile'
import { MatchesCollection } from 'src/screens/matches/collection/MatchesCollection'
import {
  ValveAutoDownloadWarning,
  shouldShowValveAutoDownloadWarning
} from 'src/screens/matches/ValveAutoDownloadWarning'
import { WelcomeMessage } from 'src/screens/matches/WelcomeMessage'
import { MeetCoachAgainLink } from 'src/screens/matches/MeetCoachAgain'
import { ProgressionReport } from 'src/screens/progression-report/ProgressionReportMatches'
import { CheckForNewMatchesControl } from 'src/screens/matches/CheckForNewMatchesControl'
import { JoinDiscordPanel } from 'src/system/DiscordPanel'
import { MissingMatchesSupport } from 'src/screens/matches/MissingMatchesSupport'
import { useMatchesUi } from 'src/hooks/use-ui'
import { FetchMoreMatchesButton } from 'src/screens/matches/FetchMoreMatchesButton'
import { GenericFeedbackButton } from 'src/screens/match/assessments/AssignmentFeedbackButton'

const welcomeDayThreshold = 3

export function Matches() {
  const { t } = useTranslation()

  const { matchesViewMode: viewMode } = useMatchesUi()
  const [setMatchesViewMode] = useMutation<
    SetMatchesViewModeMutation,
    SetMatchesViewModeMutationVariables
  >(setMatchesViewModeMutation)
  const setViewMode = (viewMode: ViewMode) => {
    setMatchesViewMode({ variables: { viewMode } })
  }

  const { matches, loading, fetchingMore, error, fetchMore, hasMoreMatches } =
    useMatches()
  const source = useReactiveVar(activeMatchSource)
  const { user } = useMaybeUser()
  const showValveAutoDownloadWarning = shouldShowValveAutoDownloadWarning(user)

  const numVisibleMatches = matches.length

  const { profile } = useProfile('cache-first')
  const accountCreatedAt = profile?.createdAt
  const showWelcomeMessage =
    !!accountCreatedAt &&
    DateTime.fromISO(accountCreatedAt).diffNow('days').days >
      -welcomeDayThreshold

  return (
    <>
      <Meta name='matches' />
      <UserAppBar />
      <Background>
        <Container>
          <ProgressionReport />
          <PageHeader>
            <div>
              <PageTitle>{t('matches:title')}</PageTitle>
              <PageSubhead>{t('matches:subhead')}</PageSubhead>
            </div>

            <HeaderRight>
              <CheckForNewMatchesControl />
            </HeaderRight>
          </PageHeader>
          <Content>
            {showValveAutoDownloadWarning && <ValveAutoDownloadWarning />}
            {showWelcomeMessage && <WelcomeMessage />}
            <StyledSourceTabs>
              <SourceTabs />
            </StyledSourceTabs>
            <ContentHeader>
              <ContentHeaderLeft>
                <NumMatches>
                  {t('matches:showing_matches', { num: numVisibleMatches })}
                </NumMatches>
                <MissingMatchesSupport />
              </ContentHeaderLeft>
              <SetViewModeButtons
                currentMode={viewMode}
                handleSetMode={setViewMode}
              />
            </ContentHeader>
            <MatchesCollection
              viewMode={viewMode}
              matches={matches}
              loading={loading}
              error={!!error}
              emptyState={<SourceEmptyState source={source} />}
            />
            <FetchMoreMatchesButton
              processing={fetchingMore}
              hasMoreMatches={hasMoreMatches}
              handleClick={fetchMore}
            />
          </Content>
          <JoinDiscordPanel
            title={t('discord_panel:questions:title')}
            description={t('discord_panel:questions:description')}
          />
          <StyledDiv>
            <StyledGenericFeedbackButton />
          </StyledDiv>
          <MeetCoachAgainLink />
        </Container>
      </Background>
    </>
  )
}
const PageHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding-top: ${(props) => props.theme.d.spacing[9]};

  align-items: flex-start;
  flex-direction: column;

  @media (min-width: ${(props) => props.theme.d.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
  }
`

const HeaderRight = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  @media (min-width: ${(props) => props.theme.d.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
  }
`

const Content = styled.div``

const StyledSourceTabs = styled.div`
  margin-bottom: ${(props) => props.theme.d.spacing[4]};
`

const ContentHeader = styled.div`
  margin-bottom: ${(props) => props.theme.d.spacing[4]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => props.theme.d.spacing[4]};
`

const ContentHeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: ${(props) => props.theme.d.spacing[3]};
  row-gap: ${(props) => props.theme.d.spacing[1]};
`

const NumMatches = styled.p`
  font-size: 16px;
  margin: 0;
`

const StyledGenericFeedbackButton = styled(GenericFeedbackButton)`
  text-align: center;
`

const StyledDiv = styled.div`
  text-align: center;
  margin-top: 20px;
`
